/**
 * Created by Thomas Mark
 * Date: 2020-05-01
 * 
 * CORDOVA-Schnittstelle
 * 
 * Enthält alle CORDOVA-Funktionen bzw. PlugIns der App.
 * 
 * Für den Fall, dass die App als PWA-App läuft, werden
 * alle CORDOVA-Funktionen in ähnlicher Funktionsweise als 
 * PWA-Funktionen implementiert.
 * 
 */
import axios from 'axios';
import FileSaver from "file-saver";
import * as utils from "./utils";
import store from '../store'
import db from './db';

/**
 * Die Funktion liest ein einzelnes Bild (PNG, JEPG) aus dem lokalen Speicher
 * 
 * - Wenn CORDOVA existiert, dann wird das Image aus dem  
 *   lokalen Speicher geladen des Endgerätes (iPad, etc.)
 *   geladen.
 * 
 * - Wenn CORDOVA nicht existiert, dann wird das Image
 *   mittels Dexie aus der IndexedDB geladen.  
 *
 * @param  {string} sID - ID des Images
 */
export const getImageFromLocalStorage = (sContainer, sFilePath, sFileName) => {
    const debug = false;

    const sID = `${sContainer}_${utils.cyrb53(sFilePath)}_${sFileName}`;
    debug && console.log('getImageFromLocalStorage() sID:', sID);

    return new Promise((resolve, reject) => {
        if (window['cordova']) {
            window.resolveLocalFileSystemURL(`cdvfile://localhost/persistent/image/${sID}`, (fileEntry) => {

                fileEntry.file((file) => {
                    let reader = new FileReader();

                    reader.onloadend = (e) => {
                        debug && console.log('getImageFromLocalStorage(success)');
                        resolve(e.target.result);
                    }
                    reader.readAsDataURL(file);
                });

            }, (error) => {
                console.log('getImageFromLocalStorage(error)', error);
                reject(error);
            });

        } else {
            db.image.where('id').equals(sID).first().then((d) => {
                if (d === undefined) {
                    resolve('');
                } else {
                    resolve(d.data);
                }
                debug && console.log('getImageFromLocalStorage(success)');
            }).catch((error) => {
                console.log('getImageFromLocalStorage(error)', error);
                reject(error);
            })
        }
    });
}

/**
 * Öffnet ein Video in einem neuen Fenster
 * 
 * - Wenn CORDOVA existiert, dann wird das Video aus dem  
 *   lokalen Speicher geladen und mit dem entsprechende 
 *   PlugIn gestartet.
 * 
 * - Wenn CORDOVA nicht existiert, dann muss das Video
 *   mit Hilfe der Komponente portfolios/MediaModal 
 *   gestartet werden. 
 *  
 * @param {string} fileName - Dateiname des Videos
 */
export const viewVideo = async (fileName) => {


    if (window['cordova']) {

        console.log('viewVideo cordova:', fileName)

        const path = window.cordova.file.applicationStorageDirectory + 'Documents/video/' + fileName;

        const uri = encodeURI(path);

        var options = {
            controls: true,
            shouldAutoClose: false,
            successCallback: () => { console.log("Video closed without error.") },
            errorCallback: (error) => { console.log("ERROR:", error) }
        };
        window.plugins.streamingMedia.playVideo(uri, options);
    }
}

/**
 * Öffnet ein PDF-Dokument in einem neuen Fenster
 * 
 * - Wenn CORDOVA existiert, dann wird das PDF-Dokument aus
 *   dem lokalen Speicher geladen und mit dem entsprechende 
 *   PlugIn gestartet.
 * 
 * - Wenn CORDOVA nicht existiert, dann muss das PDF-Dokument
 *   mit Hilfe der Komponente portfolios/MediaModal gestartet
 *   werden. 
 *
 * @param {string} fileName - Dateiname des PDF-Dokumentes
 * @param {string} viewOnly - PDF nur anzeigen. Keine Print, Share oder EMail
 */
export const viewPDF = async (fileName, viewOnly = false) => {
    const { apiURL, token, currentContainer, appModus } = store.getState().app;

    const debug = false;

    const fetchPDF_IndexedDB = async (sID) => {
        try {
            const dbFile = await db.pdf.where('id').equals(sID).first();
            if (dbFile) {
                const blob = new Blob([dbFile.data], { type: 'application/pdf' });

                if (viewOnly) {
                    window.URL = window.URL || window.webkitURL;
                    const pdf = window.URL.createObjectURL(blob);
                    viewWebsite(pdf);
                } else {
                    const downloadName = fileName.split('_').pop();

                    FileSaver.saveAs(blob, downloadName);
                }

            } else {
                console.error(`PDF [${sID}] not found!`);
            }
        } catch (error) {
            console.error(`Error reading PDF [${sID}] from IndexedDB!`, error);
        }
    }

    const fetchPDF = async (sID) => {
        const url = encodeURI(`${apiURL}/api/v1/file/${currentContainer}/draft/path/${sID}`);

        await axios({
            url: url
            , method: 'get'
            , timeout: 60 * 1000
            , headers: { 'authorization': token }
            , responseType: 'blob',
            onDownloadProgress: (progress) => {
                let currentProgress = 0;
                if (progress.total * progress.loaded !== 0) {
                    currentProgress = (100 / progress.total * progress.loaded);
                }
                this.setState({ currentProgress: currentProgress })
            }
        }).then(res => {
            const pdf = window.URL.createObjectURL(res.data);

            viewWebsite(pdf);

        }).catch((error) => {
            console.error(`can't load PDF [${url}] from API!`, error);
        });
    }

    if (window['cordova']) {
        const path = window.cordova.file.applicationStorageDirectory + 'Documents/pdf/' + fileName;

        const uri = encodeURI(path);

        const options = {
            title: ' ',
            page: 1, // immer auf 1. Seite positionieren
            documentView: { closeLabel: 'Zurück' },
            navigationView: { closeLabel: 'Fertig' },
            email: { enabled: viewOnly },
            print: { enabled: viewOnly },
            openWith: { enabled: viewOnly, doublePage: true },
            bookmarks: { enabled: true },
            search: { enabled: true },
            autoClose: { onPause: true }
        }

        var linkHandlers = [];

        if (!checkCordovaPlugin('SitewaertsDocumentViewer')) return false;

        const onError = (error) => { console.log("WARNING: PDF cannot be shown!", error) }

        const onImpossible = () => { console.log('WARNING: PDF cannot be shown!') }

        const onMissingApp = () => { console.log('WARNING: PDF Viewer App missing!') }

        const onPossible = () => {

            const onShow = () => { }
            const onClose = () => { }

            debug && console.log('OK PDF wird jetzt geöffnet...');

            window.cordova.plugins.SitewaertsDocumentViewer.viewDocument(
                uri, 'application/pdf', options, onShow, onClose, onMissingApp, onError, linkHandlers);
        }

        debug && console.log('OK PDF wird jetzt geprüft...');

        window.cordova.plugins.SitewaertsDocumentViewer.canViewDocument(
            uri, 'application/pdf', options, onPossible, onMissingApp, onImpossible, onError);
    }
    else {
        if (appModus === 'Admin') {
            fetchPDF(fileName);
        } else {
            fetchPDF_IndexedDB(fileName);
        }
    }
}

/**
 * Öffnet eine Website in einem neuen Fenster
 * 
 * - Wenn CORDOVA existiert, dann wird das Video
 *   aus dem lokalen Speicher geladen und mit dem entsprechende 
 *   PlugIn gestartet.
 * 
 * - Wenn CORDOVA nicht existiert, dann wird das Video
 *   mittels Dexie aus der IndexedDB geladen und in einem
 *   neuen Browser-Fenster angezeigt.  
 *
 * @param  {string} url - Link auf die Website
 */
export const viewWebsite = (url) => {

    if (window['cordova']) {
        console.log('CORDOVA is supported!');

        if (!window.cordova['InAppBrowser']) {
            console.log('InAppBrowser is not supported!');
            return;
        }

        const inAppBrowserRef = window.cordova.InAppBrowser.open(
            url,
            '_blank',
            'location=no,toolbartranslucent=no,fullscreen=no,toolbarposition=bottom,closebuttoncaption=Zurück,closebuttoncolor=#FFFFFF'
        );

        const exitBrowser = () => {
            console.log('Browser exit');
            inAppBrowserRef.removeEventListener('exit');
        }

        inAppBrowserRef.addEventListener('exit', exitBrowser);
    }
    else // CORDOVA existiert nicht | PWA-Variante
    {
        console.log('window.open() url:', url)
        window.open(url, '_blank')
    }
}

/**
 * E-Mail mit allen PDFs erstellen
 * 
 * Diese Funktion ermittelt alle PDF-Dokumente eines Produktes und
 * erstellt eine vorgefertigte E-Mail, die der Benutzer dann einem 
 * Interessenten zusenden kann. Die PDF-Dokumente werden der E-Mail beigefügt.
 * 
 * - In der CORDOVA-Version wird das 'socialsharing'-PlugIn verwendet.
 *   Falls diese PlugIn nicht zur Verfügung steht, so wird versucht die 
 *   die E-Mail per 'email'-Plugin zu versenden.
 * 
 * - In der PWA-Version wird versucht die PDF-Dokumente mittels 
 *   'webshare'-Funktion zu versenden. https://www.w3.org/TR/web-share/
 *  
 * @param  {string} sContainer - Container (z.B.: 'schöler', 'linde', etc.)
 * @param  {string} path - Produktpfad
 *
 */
export const sendMail = async (sContainer, path) => {
    const debug = false;

    const subject = 'Produktinformationen';
    const message = 'Sehr geehrte/r Kunde/in, \nIm Anhang erhalten Sie wie gewünscht die für Sie zusammengestellten Produktinformationen.\nBei weiteren Fragen stehe ich Ihnen jederzeit gerne zur Verfügung.';

    const items = utils.getTreePortfolio(path)
    const pdfs = items.filter(f => utils.getFileExtension(f.name) === 'pdf' && utils.checkPriceListPDF(f.name) === false)

    if (pdfs.length === 0) {
        console.log('keine PDFs gefunden!')
    }

    if (window['cordova']) {
        const localPDFs = readLocalDirectory(sContainer, 'pdf');
        debug && console.log('localPDFs:', localPDFs)

        const files = pdfs.map((d) => {
            const sID = `${sContainer}_${utils.cyrb53(d.path)}_${d.name}`;
            return window.cordova.file.applicationStorageDirectory + 'Documents/pdf/' + sID
        })

        debug && console.log('files:', files)

        if (checkWindowPlugin('socialsharing')) {

            const onSuccess = (result) => {
                console.log("Share completed? " + result.completed);
            }

            const onError = (error) => {
                console.log("Sharing failed with message: " + error);
            }

            window.plugins.socialsharing.shareWithOptions({
                message: message,
                subject: subject,
                files: files
            }, onSuccess, onError);

        } else if (checkCordovaPlugin('email')) {

            window.cordova.plugins.email.open({
                app: 'mailto',
                subject: subject,
                body: message,
                attachments: files
            });
        } else {
            console.log('WRANUNG: Es existiert kein PlugIn um E-Mails zu versenden!');
        }

    } else {

        const getPDF = (pdf) => {
            return new Promise(async function (resolve, reject) {
                const sID = `${sContainer}_${utils.cyrb53(pdf.path)}_${pdf.name}`;
                debug && console.log(`getPDF(${sID})`);

                try {
                    const dbfile = await db.pdf.where('id').equals(sID).first()
                    if (dbfile) {
                        debug && console.log('getPDF(success):', dbfile, pdf.name, pdf.size)
                        const blobFile = new File([dbfile.data], pdf.name, { type: 'application/pdf' });

                        resolve(blobFile);
                    }
                } catch (error) {
                    console.log('getPDF(error):', error);
                    reject(error)
                }
            })
        }

        Promise.all(pdfs.map(getPDF)).then((blobList) => {
            if (!navigator.canShare || !navigator.canShare({ files: blobList })) {
                debug && console.log('sendMail(error 1): Unsupported share feature');
                return;
            }
            try {
                navigator.share({
                    title: subject,
                    text: message,
                    files: blobList
                })
                    .then(() => console.log('sendMail(success): Successful share'))
                    .catch((error) => console.log('sendMail(error 2): Error sharing', error));

            } catch (error) {
                console.log('sendMail(error 3): Error sharing', error);
            }

        }).catch((error) => {
            console.log('sendMail(error 4): Error sharing', error)
        })
    }
}

/**
 * E-Mail mit Feedback senden
 */
export const sendFeedback = (feedback, toMailAddress) => {

    const to = toMailAddress;
    const subject = `Feedback ${process.env.REACT_APP_NAME} App Version: ${process.env.REACT_APP_VERSION}`
    const body = feedback

    if (window['cordova']) {
        if (!checkCordovaPlugin('email')) return false;

        window.cordova.plugins.email.open({ to, subject, body })
    } else {
        window.open("mailto:" + to + "?subject=" + subject + "&body=" + body);
    }
}

/**
 * Schreibt eine JSON-Datei in den lokalen Speicher
 * 
 * Unabhängig davon, ob CORDOVA existiert, werden lokale 
 * JSON-Dateien immer in der IndexedDB gespeichert.
 *
 * @param  {string} sFileName - Bezeichnung der JSON-Datei
 * @param  {object} data - Object im JSON-Format
 */
export const writeLocalJSON = (sContainer, sFileName, data) => {
    const sID = (sContainer === null) ? sFileName : `${sContainer}_${sFileName}`;

    return new Promise((resolve, reject) => {

        db.transaction('rw', db.json, () => {
            //console.log('put ' + sFileName + ' to IndexedDB')

            db.json.put({
                id: sID,
                name: sFileName,
                container: sContainer,
                data: data
            });
            resolve(data);
        }).catch((error) => {
            console.log('ERROR: transaction db.json.put()', sFileName, error)
            reject(error);
        });
    });
}

/**
 * Liest eine JSON-Datei aus dem lokalen Speicher
 * 
 * Unabhängig davon, ob CORDOVA existiert, werden lokale 
 * JSON-Dateien immer in der IndexedDB gespeichert.
 *
 * @param  {string} sContainer - Container (z.B.: 'schöler', 'linde', etc.)
 * @param  {string} sFileName - Bezeichnung der JSON-Datei
 */
export const readLocalJSON = (sContainer, sFileName) => {
    const sID = (sContainer === null) ? sFileName : `${sContainer}_${sFileName}`;

    return new Promise(async (resolve, reject) => {

        try {
            const dbJson = await db.json.where('id').equals(sID).first()
            if (dbJson) {
                resolve(dbJson.data);
            } else {
                reject(new Error(`readLocalJSON(1) file [${sID}] not found!`));
            }
        } catch (error) {
            reject(new Error(`readLocalJSON(2) file [${sID}] not found!`));
        }
    });
}

/**
 * Die Funktion erstellt ein Verzeichnis im LocalFileSystem
 * 
 */
export const createLocalDirectory = (sDirName) => {
    return new Promise((resolve, reject) => {

        if (window['cordova']) {
            if (!checkLocalFileSystem()) {
                reject(new Error('LocalFileSystem is not supported!'))
            }

            window.requestFileSystem(window.LocalFileSystem.PERSISTENT, 0, (fs) => {

                fs.root.getDirectory(sDirName, { create: true }, (data) => {
                    console.log(`create ${sDirName}-Folder`);
                    resolve();
                }, (error) => {
                    reject(new Error(`create ${sDirName}-Folder failed!`, error));
                });
            }, (error) => {
                reject(new Error(`${sDirName}-Folder failed!`, error));
            });
        }
        else {
            resolve(null);
        }
    })
}

/**
 * Die Funktion gibt alle Dateien eines lokalen Verzeichnisses zurück 
 * 
 * - Wenn CORDOVA existiert, dann werden die Dateien im lokalen Speicher 
 *   gelesen.
 * 
 * - Wenn CORDOVA nicht existiert, dann werden die Dateien
 *   mittels Dexie aus der entsprechenden Tabelle der IndexedDB gelesen.
 * 
 *  @param  {string} sContainer - Container (z.B.: 'schöler', 'linde', etc.)
 *  @param  {string} sDirName - Verzeichnis
 *  @return {array} localFiles - { id, name }
 *
 */
export const readLocalDirectory = (sContainer, sDirName) => {
    const debug = false;
    debug && console.log(`readLocalDirectory(${sContainer}, ${sDirName})`)
    return new Promise(async (resolve, reject) => {

        if (window['cordova']) {
            const path = `cdvfile://localhost/persistent/${sDirName}`;

            if (!checkLocalFileSystem()) {
                reject(new Error('LocalFileSystem is not supported!'))
            }

            window.resolveLocalFileSystemURL(path, (fs) => {
                let reader = fs.createReader();

                reader.readEntries((localFiles) => {
                    //console.log(`readLocalDirectory() directory [${sDirName}] found localFiles:`, localFiles)
                    resolve(localFiles.map((d) => ({ id: d.name, name: d.name })));

                }, (error) => {
                    reject(new Error(`readLocalDirectory(error) [${sDirName}] not found!`, error));
                });
            }, (error) => {
                reject(new Error(`readLocalDirectory(error) [${sDirName}]`, error));
            });

        } else { // IdexedDB / Dexie

            try {
                let localFiles = [];
                if (sDirName === 'pdf') localFiles = await db.pdf.orderBy('id').keys();
                if (sDirName === 'image') localFiles = await db.image.orderBy('id').keys();
                if (sDirName === 'video') localFiles = await db.video.orderBy('id').keys();

                if (localFiles) {
                    resolve(localFiles.map((id) => ({ id: id, name: id })));
                } else {
                    reject(new Error(`readLocalDirectory(error) [${sDirName}] not found!`));
                }
            } catch (error) {
                console.log(new Error(`readLocalDirectory() no files found in [${sDirName}]`, error))
                resolve([]);
            }
        }
    });
}

/**
 * Die Funktion ermittelt die Dateien, die bereits heruntergeladen wurden.
 * 
 * - Wenn CORDOVA existiert, dann werden die Dateien im lokalen Speicher 
 *   gelesen.
 * 
 * - Wenn CORDOVA nicht existiert, dann werden die Dateien
 *   mittels Dexie in der IndexedDB gelesen.
 * 
 *  @param  {string} sContainer - Container (z.B.: 'schöler', 'linde', etc.)
 *
 */
export const readLocalPortfolios = (sContainer = null) => {
    return new Promise(async (resolve, reject) => {

        try {
            const allPortfolios = await db.portfolio.toArray();
            if (allPortfolios) {
                if (sContainer === null) {
                    resolve(allPortfolios);
                } else {
                    let newPortfolioList = [];
                    allPortfolios.forEach((d) => {
                        const splitID_array = d.id.split('_');
                        const splitID_container = (splitID_array.length > 0) ? splitID_array[0] : null;
                        if (splitID_container.localeCompare(sContainer) === 0) {
                            newPortfolioList.push(d);
                        }
                    });
                    resolve(newPortfolioList);
                }

            } else {
                reject(new Error(`readLocalPortfolios(1) no portfolios found!`));
            }
        } catch (error) {
            reject(new Error(`readLocalPortfolios(2) no portfolios found!`, error));
        }
        /* alter code
                if (window['cordova'] ) {
                    const path = `cdvfile://localhost/persistent/portfolio`;
        
                    if (!checkLocalFileSystem()) {
                        reject(new Error('LocalFileSystem is not supported!'))
                    }
        
                    window.resolveLocalFileSystemURL(path, (fs) => {
                        let reader = fs.createReader();
        
                        reader.readEntries((localPortfolios) => {
                            Promise.all(localPortfolios.map(readLocalFileByURL)).then((allPortfolios) => {
        
                                if (sContainer === null) {
                                    resolve(allPortfolios);
                                } else {
                                    let newPortfolioList = [];
                                    allPortfolios.forEach((d) => {
                                        const splitID_array = d.id.split('_');
                                        const splitID_container = (splitID_array.length > 0) ? splitID_array[0] : null;
        
                                        if (splitID_container.localeCompare(sContainer) === 0) {
                                            newPortfolioList.push(d);
                                        }
                                    });
                                    resolve(newPortfolioList);
                                }
        
                            }).catch((error) => {
                                reject(new Error(`readLocalPortfolios(1) no portfolios found!`, error));
                            })
        
                        }, (error) => {
                            reject(new Error(`readLocalPortfolios(2) no portfolios found!`, error));
                        });
                    }, (error) => {
                        reject(new Error(`readLocalPortfolios(3) no portfolios found!`, error));
                    });
        
                } else {
        
                }
            */
    });
}

/**
 * Schreibt eine Datei in den lokalen Speicher
 * 
 * - Wenn CORDOVA existiert, dann wird die Datei
 *   im lokalen Speicher abgelegt.
 * 
 * - Wenn CORDOVA nicht existiert, dann wird die Datei
 *   mittels Dexie in der IndexedDB des Browsers abgelegt.
 *
 * Beispiel: https://javascript.info/promise-basics
 * 
 * @param  {string} sContainer - Container
 * @param  {string} sDirName - Verzeichnis
 * @param  {string} sFilePath - Pfad der Datei
 * @param  {string} sFileName - Name der Datei
 * @param  {object} data - Daten im BLOB-Format
 */
export const writeLocalFile = (sContainer, sDirName, sFilePath, sFileName, data) => {
    //console.log(`put ${sContainer}/${sDirName} -- ${sFilePath}/${sFileName} to LocalStorage`);

    return new Promise((resolve, reject) => {

        // portfolio-Daten werden immer in der IndexedDB abgelegt
        if (sDirName === 'portfolio') {
            db.transaction('rw', db.portfolio, () => {
                db.portfolio.put({ ...data, container: sContainer });
                resolve(sFileName);
            }).catch((error) => {
                console.log('ERROR: transaction error!', sFileName, error)
                reject(error);
            });
            return;
        }

        if (window['cordova']) {

            console.log(`cordova put ${sContainer}/${sDirName} -- ${sFilePath}/${sFileName} to LocalStorage`);

            if (!checkLocalFileSystem()) {
                reject(new Error('LocalFileSystem is not supported!'))
            }

            if (sDirName === 'video') {
                window.resolveLocalFileSystemURL(`cdvfile://localhost/persistent/${sDirName}`, (directoryEntry) => {
                    directoryEntry.getFile(sFileName, { create: true }, (fileEntry) => {
                        fileEntry.createWriter((fileWriter) => {
                            try {
                                fileWriter.onwriteend = (eEvent) => {
                                    //console.log('writeLocalFile(success)');
                                    resolve(fileWriter.localURL);
                                };

                                fileWriter.onerror = (error) => {
                                    console.log('writeLocalFile:write(onerror)', error);
                                    reject(new Error('Fehler 1!', error));
                                };

                                if (sDirName === 'portfolio') {
                                    const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
                                    fileWriter.write(blob);
                                } else {
                                    fileWriter.write(data)
                                }
                            } catch (error) {
                                console.error('writeLocalFile:write(error)', error);
                                reject(new Error('Fehler 2!', error));
                            }
                        },
                            (error) => {
                                console.error('writeLocalFile:createWriter(error)', error);
                                reject(new Error('Fehler 3!', error));
                            }
                        );
                    }, (error) => {
                        console.error('writeLocalFile:getFile(error)', error);
                        reject(new Error('Fehler 4!', error));
                    });
                }, (error) => {
                    console.error('writeLocalFile:resolveLocalFileSystemURL(error)', error);
                    reject(new Error('Fehler 5!', error));
                });
            }

            if (sDirName === 'image') {
                window.resolveLocalFileSystemURL(`cdvfile://localhost/persistent/${sDirName}`, (directoryEntry) => {
                    const sID = `${sContainer}_${utils.cyrb53(sFilePath)}_${sFileName}`;

                    directoryEntry.getFile(sID, { create: true }, (fileEntry) => {
                        fileEntry.createWriter((fileWriter) => {

                            data2type(data, 'ArrayBuffer').then((base64) => {

                                fileWriter.onwriteend = (eEvent) => {
                                    resolve(fileWriter.localURL);
                                };

                                fileWriter.onerror = (error) => {
                                    console.error('writeLocalFile(error)', error);
                                    reject(error);
                                };

                                fileWriter.write(base64);

                            }).catch((error) => {
                                console.log('ERROR: transaction error!', sFileName, error)
                                reject(error);
                            })
                        },
                            (error) => {
                                console.error('writeLocalFile(error) onErrorCreateFile', error);
                                reject(error);
                            });
                    }, (error) => {
                        console.error('writeLocalFile(error 3) onErrorCreateFile', error);
                        reject(new Error('Fehler 3!', error));
                    });
                }, (error) => {
                    console.error('writeLocalFile(error 4) onErrorCreateFile', error);
                    reject(new Error('Fehler 4!', error));
                });
            }

            if (sDirName === 'pdf') {
                window.resolveLocalFileSystemURL(`cdvfile://localhost/persistent/${sDirName}`, (directoryEntry) => {
                    const sID = `${sContainer}_${utils.cyrb53(sFilePath)}_${sFileName}`;

                    directoryEntry.getFile(sID, { create: true }, (fileEntry) => {
                        fileEntry.createWriter((fileWriter) => {

                            fileWriter.onwriteend = (eEvent) => {
                                //console.log('writeLocalFile(success)');
                                resolve(fileWriter.localURL);
                            };

                            fileWriter.onerror = (error) => {
                                console.error('onErrorCreateWriter', error);
                                reject(error);
                            };

                            fileWriter.write(data);
                        },
                            (error) => {
                                console.error('writeLocalFile(error) onErrorCreateFile', error);
                                reject(error);
                            });
                    }, (error) => {
                        console.error('writeLocalFile(error 3) onErrorCreateFile', error);
                        reject(new Error('Fehler 3!', error));
                    });
                }, (error) => {
                    console.error('writeLocalFile(error 4) onErrorCreateFile', error);
                    reject(new Error('Fehler 4!', error));
                });
            }

        } else {

            if (sDirName === 'video') {
                db.transaction('rw', db.video, () => {
                    db.video.put({
                        id: sFileName,
                        container: sContainer,
                        name: sFileName,
                        data: data
                    });

                    resolve(sFileName);
                }).catch((error) => {
                    console.log('ERROR: transaction error!', sFileName, error);
                    console.log(`Video [${sFileName}] konnte nicht in der Datenbank gespeichert werden!`);
                    reject(error);
                })
            }

            if (sDirName === 'image') {
                const sID = `${sContainer}_${utils.cyrb53(sFilePath)}_${sFileName}`;

                db.transaction('rw', db.image, () => {
                    db.image.put({
                        id: sID,
                        container: sContainer,
                        data: data
                    });
                    resolve(sFileName);
                }).catch((error) => {
                    console.log('ERROR: transaction error!', sFileName, error)
                    console.log(`Bild [${sFileName}] konnte nicht in der Datenbank gespeichert werden!`);
                    reject(error);
                })
            }

            if (sDirName === 'pdf') {
                const sID = `${sContainer}_${utils.cyrb53(sFilePath)}_${sFileName}`;

                db.transaction('rw', db.pdf, () => {
                    db.pdf.put({
                        id: sID,
                        container: sContainer,
                        data: data
                    });

                    resolve(sFileName);
                }).catch((error) => {
                    console.log('ERROR: transaction error!', sFileName, error)
                    console.log(`PDF-Dokument [${sFileName}] konnte nicht in der Datenbank gespeichert werden!`);
                    reject(error);
                })
            }
        }
    })
}

/**
 * Ließt eine Datei aus dem lokalen Speicher
 * 
 * - Wenn CORDOVA existiert, dann wird die Datei
 *   aus dem lokalen Speicher gelesen.
 * 
 * - Wenn CORDOVA nicht existiert, dann wird die Datei
 *   mittels Dexie aus der IndexedDB gelesen.
 *
 * @param  {string} sContainer - Container
 * @param  {string} sDirName - Verzeichnis
 * @param  {string} sFilePath - Pfad der Datei
 * @param  {string} sFileName - Name der Datei
 * @param  {object} data - Daten im BLOB-Format
 * 
 */
export const readLocalFile = (sContainer, sDirName, sFilePath, sFileName, type = 'DataURL') => {
    let debug = false;

    debug && console.log(`get ${sContainer}/${sDirName} -- ${sFilePath}/${sFileName} from LocalStorage`);

    return new Promise((resolve, reject) => {

        const sID = `${sContainer}_${utils.cyrb53(sFilePath)}_${sFileName}`;
        debug && console.log('sID:', sID);

        if (window['cordova']) {

            window.resolveLocalFileSystemURL(`cdvfile://localhost/persistent/${sDirName}/${sID}`, (fileEntry) => {

                fileEntry.file((file) => {
                    let reader = new FileReader();

                    reader.onloadend = (e) => {
                        debug && console.log('readLocalFile(success)');
                        resolve(e.target.result);
                    }

                    if (type === 'Text') reader.readAsText(file);
                    if (type === 'DataURL') reader.readAsDataURL(file);
                    if (type === 'ArrayBuffer') reader.readAsArrayBuffer(file);
                    if (type === 'BinaryString') reader.readAsBinaryString(file);
                });

            }, (error) => {
                debug && console.log('readLocalFile(error)', error);
                reject(error);
            });

        } else {
            if (sDirName === 'image') {
                db.image.where('id').equals(sID).first().then((img) => {
                    if (img === undefined) {
                        debug && console.log('readLocalFile(error) Image not found!');
                        resolve();
                    } else {
                        debug && console.log('readLocalFile(success)');
                        resolve(img.data);
                    }

                }).catch((error) => {
                    debug && console.log('readLocalFile(error)', error);
                    resolve(error);
                })
            }
        }
    })
}

export const readLocalFileByURL = (d) => {
    return new Promise(async (resolve, reject) => {

        window.resolveLocalFileSystemURL(d.nativeURL, (fileEntry) => {

            fileEntry.file((file) => {
                //console.log('--> file:', file)
                let reader = new FileReader();

                reader.onloadend = (e) => {
                    if (e.target.result.length > 0) {
                        resolve({ ...JSON.parse(e.target.result), id: d.name });
                    } else {
                        resolve({});
                    }
                }
                reader.readAsText(file);
            });

        }, (error) => { reject(new Error('localFiles not found!', error)); });
    });
}

/**
 * prüft, ob ein bestimmtes CORDOVA-Plugin eistiert
 * 
 * @param  {string} plugin - PlugIn-Bezeichnung
 * @return {boolean} true - PlugIn existiert
 */
export const checkCordovaPlugin = (plugin) => {

    if (!window['cordova']) {
        console.log('CORDOVA is not supported!');
        return false;
    }

    if (!window.cordova['plugins']) {
        console.log('Plugins is not supported!');
        return false;
    }

    if (!window.cordova.plugins[plugin]) {
        console.log('Plugin [' + plugin + '] is not supported!');
        return false;
    }

    return true;
}

/**
 * prüft, ob ein bestimmtes Window-Plugin eistiert
 * 
 * @param  {string} plugin - PlugIn-Bezeichnung
 * @return {boolean} true - PlugIn existiert
 */
export const checkWindowPlugin = (plugin) => {

    if (!window['plugins']) {
        console.log('Plugins is not supported!');
        return false;
    }

    //console.log('checkCordovaPlugin() plugins:', window.cordova.plugins);

    if (!window.plugins[plugin]) {
        console.log('Plugin [' + plugin + '] is not supported!');
        return false;
    }

    return true;
}

/**
 * prüft, ob ein lokales Dateisystem eistiert
 * 
 * @return {boolean} true - resolveLocalFileSystemURL existiert
 */
export const checkLocalFileSystem = () => {

    if (!window["resolveLocalFileSystemURL"]) {
        //console.log('window.resolveLocalFileSystemURL is not supported!');
        return false;
    }
    return true;
}

/**
 * Die Funktion sorgt dafür, dass die Synchronisation
 * im Hintergrund verarbeitet wird.
 * 
 * ACHTUNG: Es ist ganz wichtig, dass bei der iOS Einstellung: 
 *     
 *     background modes -> Background processing 
 * 
 * das Häkchen gesetzt ist
 * 
 * LINK: https://stackoverflow.com/questions/67571345/ios-iphone-11-flashing-black-and-white-while-browsler-is-constantly-reloading-r
 *
 * @param  {boolean} flag - true/false
 */
export const runInBackground = (flag) => {

    if (window['cordova']) {
        if (!checkCordovaPlugin('backgroundMode')) return false;

        window.cordova.plugins.backgroundMode.setEnabled(flag);
    }
}

export const isActiveBackground = () => {

    if (window['cordova']) {
        //const isActive = window.cordova.plugins.backgroundMode.isActive();
        //console.log('CORDOVA: backgroundMode:', isActive);
    }
}

export const data2type = (data, type) => {

    return new Promise((resolve, reject) => {
        try {
            let blob = new Blob([data])

            let reader = new FileReader();
            reader.onload = function (event) {
                resolve(event.target.result);
            }

            if (type === 'Text') reader.readAsText(blob);
            if (type === 'DataURL') reader.readAsDataURL(blob);
            if (type === 'ArrayBuffer') reader.readAsArrayBuffer(blob);
            if (type === 'BinaryString') reader.readAsBinaryString(blob);

        } catch (error) {
            console.log('data2type(error)', error);
            reject(error);
        }
    })
}

/**
 * Diese Funktion durchläuft die Liste aller Images 'fileList' und überträgt 
 * jedes einzelne lokal gespeicherte Image in den REDUX-Store.
 * 
 * @param {string} sContainer - Container/Katalog (z.B.: linde, schöler, etc.)
 * @param {array} fileList - Liste aller Images
 */
export const getAllLocalImages = (sContainer, fileList) => {
    const debug = false;

    debug && console.log('getAllLocalImages()', fileList);

    return new Promise(function (resolve, reject) {

        let maxBulkSize = 1; // immer nur eine einzelne Datei verarbeiten
        let nextBulkStart = 0;

        let imageList = [];

        // ###############################################################
        // 
        // HELPER - SECTION
        // 
        // ###############################################################

        /**
         * einzelne Datei aus dem lokalen Speicher laden und in die 
         * imageList übertragen
         *
         * @param {object} d - Image-Datensatz
         */
        const getImageFile = (d) => {

            return new Promise(function (resolve, reject) {
                const name = d.name.endsWith('pdf') ? d.name.replace('pdf', 'png') : d.name;

                readLocalFile(sContainer, 'image', d.path, name).then((data) => {
                    debug && console.log(`   add image ${name}] to imageList`);

                    imageList.push({ id: d.id, path: d.path, name: name, data: data })
                    resolve();
                }).catch((error) => {
                    debug && console.error('getAllLocalImages:getFile(error)', error);
                    resolve();
                })
            })
        }

        /**
         * Nächstes Paket mit Dateien erstellen
         * In diesem Fall wird jedoch immer nur ein einzelne Datei verarbeitet,
         * da sich herausgestellt hat, dass es keine Performance Verbesserung
         * ergibt, wenn mehrere Dateien paralell verarbeit werden. 
         */
        const getNextBulk = () => {
            let bulkList = [];
            let count = 0;

            for (let i = nextBulkStart; i <= fileList.length; i++) {
                if (count >= maxBulkSize || fileList.length === parseInt(i)) {
                    nextBulkStart += count;
                    return bulkList;
                }
                bulkList.push(fileList[i]);
                count++;
            }
            return [];
        }

        /**
         * Paket mit Dateien verarbeiten
         * Die Funktion arbeitet rekursiv. Sobald ein Paket verarbeitet
         * wurde, wird das nächste Paket mittels getNextBulk() ermittelt
         * und solange abgearbeitet, bis alle Dateien verarbeitet wurden.
         */
        const doBulkAddFiles = () => {

            let syncBulkList = getNextBulk();

            if (syncBulkList.length > 0) {
                Promise.all(syncBulkList.map(getImageFile)).then(() => {

                    doBulkAddFiles();

                }).catch((err) => {
                    console.log('getAllLocalImages(error)', err);
                    reject(err);
                })
            } else {
                // ########################################
                // FINALE
                // ########################################
                debug && console.log('getAllLocalImages(success)');

                resolve(imageList);
            }
        }

        // ###############################################################
        // 
        // MAIN - SECTION
        //
        // START - processing first Document
        // ###############################################################

        doBulkAddFiles();
    })
}

/**
 * loadImageBuffer() - #imageBuffer mit Images füllen.
 * 
 * Diese Funktion ermittelt alle Images eines Containers und speichert diese
 * dann direkt im REDUX-Store in dem Objekt 'imageBuffer'. Diese Vorgehensweise
 * hat einen gravierenden Performance-Vorteil, da es schneller ist ein Image
 * aus dem REDUX-Store zu laden, als aus dem lokalen Speicher.  
 * 
 * @param {string} sContainer - Container/Katalog (z.B.: linde, schöler, etc.)
 * @param {array} newPortfolioList - Liste aller Portfolios
 */
export const loadImageBuffer = (sContainer, newPortfolioList) => {
    const { imageBuffer } = store.getState().app;

    return new Promise(async function (resolve, reject) {
        const message = '\n\nIMAGE BUFFER: loadImageBuffer() loading...\n';
        console.log(message); utils.logHeap(message);

        const pngFiles = utils.getFileList(sContainer, newPortfolioList, 'png');
        const jpgFiles = utils.getFileList(sContainer, newPortfolioList, 'jpg');
        const pdfFiles = utils.getFileList(sContainer, newPortfolioList, 'pdf');
        const allFiles = [...pngFiles, ...jpgFiles, ...pdfFiles];

        await getAllLocalImages(sContainer, allFiles).then((containerImages) => {
            imageBuffer[sContainer] = containerImages;
            const message = '\n\nIMAGE BUFFER: loadImageBuffer() loaded.\n';
            console.log(message); utils.logHeap(message);
            resolve()
        }).catch((error) => {
            console.log('\n\nIMAGE BUFFER: loadImageBuffer(error)\n', error);
            reject();
        })
    })
}

/**
 * getImageFromBuffer() - #imageBuffer
 * 
 * Die Funktion sucht ein bestimmtes Bild im #imageBuffer
 * 
 * @param  {string} path - pfad
 * @param  {string} name - name
 * @return {object} - Image 
 */
export const getImageFromBuffer = (path, name) => {
    const { currentContainer, imageBuffer } = store.getState().app;

    let sID = `${currentContainer}/${path}/${name}`;

    if (imageBuffer[currentContainer] === undefined) {
        //console.log(`WARNING: ImageContainer [${currentContainer}] not found!`);
        return null;
    }

    const images = imageBuffer[currentContainer].filter(f => f.id.localeCompare(sID) === 0)
    if (images.length > 0) {
        return images[0].data;
    }

    //console.log(`WARNING: Image ${path}/${name} not found!`);
    return null;
}

/**
 * Die Funktion ermittelt die Größe einer Datei im lokalen Dateisystem.
 * 
 * @return {string} path - vollständiger Dateipfad
 * 
 * ACHTUNG: Funktion wird zur Zeit nicht verwendet.
 */
export const getFileSize = (path) => {

    return new Promise((resolve, reject) => {

        if (window['cordova']) {
            window.resolveLocalFileSystemURL(path, (fileEntry) => {
                fileEntry.file((fileObj) => {
                    resolve(fileObj);
                },
                    (err) => {
                        reject(err);
                    });
            },
                (err) => {
                    reject(err);
                });

        } else {
            resolve(null);
        }
    });
}
